import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import * as actions from "../../../store/slices/AssessmentSlice";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { FetchAIAssessmentPayloadTypes, SegmentQuestions } from "../../../types/assessment";



const useAssessmentAISummary = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [chatInput, setChatInput] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [playingSegment, setPlayingSegment] = useState<string | null>(null);
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<string>>(
    new Set()
  );
  const [chatMessages, setChatMessages] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);
  const [toggledViews, setToggledViews] = useState<Set<string>>(new Set());
  const [selectedSegments, setSelectedSegments] = useState<Set<string>>(
    new Set()
  );
  const [selectedViews, setSelectedViews] = useState<Set<string>>(
    new Set(["ai-summary", "positive", "negative"])
  );
  const token = localStorage.getItem("token");
  const health_coach_id = localStorage.getItem("health_coach_id");
  const patientInfo = JSON.parse(localStorage.getItem("PatientInfo") || "{}");
  const {
    assessmentAISummary: { data, isLoading, errors },
  } = useSelector((state: RootState) => state.assessment);

  const fetchAIAssessmentSummary = (assessment_type: string) => {
    const finalPayload: FetchAIAssessmentPayloadTypes = {
      assessment_id: assessment_type,
    //   phone_number: patientInfo?.contact_no,
      patient_id: params?.patientId || patientInfo?.patient_id,
    };
    dispatch(actions.getAssessmentAISummaryRequest(finalPayload));
  };

  const formatTitle = (name: string) => {
    return name
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const views = [
    { label: "AI Summary", value: "ai-summary" },
    { label: "Positive Aspects", value: "positive" },
    { label: "Negative Aspects", value: "negative" },
  ];

  // Helper function to split text on newlines or numbered lists
const splitTextIntoPoints = (text: string): string[] => {
    if (!text) return [];
    
    // First, split by newlines to handle multi-line text
    const lines = text.split('\n');
    const points: string[] = [];
    
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;
      
      // Check if the line contains multiple numbered points (e.g., "1. Point one. 2. Point two.")
      const numberedPoints = trimmedLine.match(/\d+\.\s*[^1-9]*?(?=\d+\.|$)/g);
      
      if (numberedPoints && numberedPoints.length > 1) {
        // Multiple numbered points on the same line
        numberedPoints.forEach(point => {
          const cleanPoint = point.replace(/^\d+\.\s*/, '').trim();
          if (cleanPoint) {
            points.push(cleanPoint);
          }
        });
      } else {
        // Single numbered point or non-numbered text
        const numberedMatch = trimmedLine.match(/^\d+\.\s*(.+)$/);
        if (numberedMatch) {
          // If it's a numbered point, add it as a separate point
          points.push(numberedMatch[1].trim());
        } else {
          // If it's not numbered, add the whole line
          points.push(trimmedLine);
        }
      }
    });
    
    return points.filter(point => point.length > 0);
  };

// Not is used as of now
const segmentQuestions: SegmentQuestions = {
    "1": [
      { id: "q1", question: "What is your current age?", type: "text", value: "" },
      {
        id: "q2",
        question: "Do you have any chronic conditions?",
        type: "multiselect",
        options: ["Diabetes", "Hypertension", "Heart Disease", "Arthritis", "None"],
        value: [],
      },
      { id: "q3", question: "Current medications?", type: "text", value: "" },
    ],
    "2": [
      {
        id: "q4",
        question: "What are your primary health goals?",
        type: "multiselect",
        options: ["Weight Loss", "Muscle Gain", "Better Energy", "Improved Sleep", "Stress Management"],
        value: [],
      },
      {
        id: "q5",
        question: "How motivated are you on a scale of 1-10?",
        type: "dropdown",
        options: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"],
        value: "",
      },
    ],
    "3": [
      {
        id: "q6",
        question: "How many meals do you eat per day?",
        type: "dropdown",
        options: ["1", "2", "3", "4", "5+"],
        value: "",
      },
      { id: "q7", question: "What time do you usually eat breakfast?", type: "text", value: "" },
      {
        id: "q8",
        question: "Do you have any dietary restrictions?",
        type: "multiselect",
        options: ["Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Keto", "None"],
        value: [],
      },
    ],
  };

  return {
    data,
    views,
    token,
    errors,
    location,
    chatInput,
    isLoading,
    isEditMode,
    chatMessages,
    toggledViews,
    selectedViews,
    playingSegment,
    health_coach_id,
    selectedSegments,
    segmentQuestions,
    expandedTranscripts,
    // Functions
    navigate,
    formatTitle,
    setChatInput,
    setIsEditMode,
    setChatMessages,
    setToggledViews,
    setSelectedViews,
    setPlayingSegment,
    setSelectedSegments,
    splitTextIntoPoints,
    setExpandedTranscripts,
    fetchAIAssessmentSummary,
  };
};

export default useAssessmentAISummary;
